// The game reads the world through a Feed: views to render, events to react to, and a way to send this player's
// inputs. LocalFeed runs shared/table.ts (the sim + bots) in the page — the offline mode and the ?solo=1 lab; the
// network feed (client/src/net/) implements the same interface against the Colyseus room.

import type { PublicView, PrivateView, SimEvent, Cosmetics, SeatInput, Vec3 } from "../../shared/sim";
import type { Call } from "../../shared/tuning";

export interface Feed {
  readonly online: boolean;
  /** advance (local: step the sim; net: interpolate), returns false until the first view exists */
  update(dtSec: number, nowMs: number): boolean;
  pub(): PublicView;            // interpolated for rendering
  priv(): PrivateView;
  drain(): SimEvent[];          // events since the last drain (public + mine)
  input(i: SeatInput): void;
  pop(id: number, aim: Vec3 | null): void;
  mark(id: number, aim: Vec3): void;
  whistle(call?: Call): void;
  humans(): number;             // real people at this table (incl. me)
  roomCode(): string;
  leave(): void;
}

import { Table, eventsFor } from "../../shared/table";
import { DT } from "../../shared/sim";

export class LocalFeed implements Feed {
  readonly online = false;
  private t: Table; private seat = 0; private acc = 0; private evs: SimEvent[] = [];
  private prevPub: PublicView | null = null; private curPub: PublicView;
  constructor(info: { name: string; pid: string; cos: Cosmetics; newcomer: boolean } | null, seed = (Math.random() * 1e9) | 0) {
    this.t = new Table(seed, { wallMs: Date.now() });
    this.seat = this.t.freeSeatIndex(); if (this.seat < 0) this.seat = 1;
    if (info) this.t.seatHuman(this.seat, info);      // null = a spectator table: every seat a bot (the menu's live backdrop)
    this.t.sim.wallMs = Date.now();
    this.t.sim.restartRound();
    this.t.pub = this.t.sim.publicView();
    this.curPub = this.t.pub;
  }
  update(dt: number, nowMs: number) {
    this.acc += Math.min(dt, 0.25);
    while (this.acc >= DT) {
      this.acc -= DT;
      this.t.sim.wallMs = nowMs;
      this.prevPub = this.curPub;
      const ev = this.t.step();
      this.curPub = this.t.pub;
      for (const e of eventsFor(ev, this.seat)) this.evs.push(e);
    }
    return true;
  }
  /** interpolate body positions between the last two steps (render at 60+ fps from a 20 Hz sim) */
  pub(): PublicView {
    const a = this.prevPub, b = this.curPub; if (!a) return b;
    const k = Math.min(1, this.acc / DT);
    const prev = new Map(a.bodies.map((x) => [x.id, x]));
    return { ...b, bodies: b.bodies.map((x) => { const p = prev.get(x.id); if (!p) return x;
      let dh = x.h - p.h; while (dh > Math.PI) dh -= 2 * Math.PI; while (dh < -Math.PI) dh += 2 * Math.PI;
      return { ...x, x: p.x + (x.x - p.x) * k, z: p.z + (x.z - p.z) * k, h: p.h + dh * k }; }) };
  }
  priv() { return this.t.sim.privateView(this.seat); }
  drain() { const e = this.evs; this.evs = []; return e; }
  input(i: SeatInput) { this.t.sim.setInput(this.seat, i); }
  pop(id: number, aim: Vec3 | null) { this.t.sim.pop(this.seat, id, aim); }
  mark(id: number, aim: Vec3) { this.t.sim.mark(this.seat, id, aim); }
  whistle(call?: Call) { this.t.sim.whistleCall(this.seat, call); }
  humans() { return 1; }
  roomCode() { return ""; }
  leave() {}
}
